import React, { useState, useRef, useEffect } from 'react';
import { ArrowLeft, Loader2 } from 'lucide-react';
import DebatePhaseBar from './DebatePhaseBar';
import BriefingPanel from './BriefingPanel';
import PositionsPanel from './PositionsPanel';
import ClashView from './ClashView';
import VerdictPanel from './VerdictPanel';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const WarRoomSession = ({ onExit, summonedCouncilGods = [] }) => {
  const [phase, setPhase] = useState(null); // null = topic input
  const [phasesCompleted, setPhasesCompleted] = useState([]);
  const [sessionId, setSessionId] = useState(null);
  const [topic, setTopic] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // Phase data
  const [displayContext, setDisplayContext] = useState(null);
  const [briefing, setBriefing] = useState(null);
  const [positions, setPositions] = useState(null);
  const [clashRounds, setClashRounds] = useState([]);
  const [verdict, setVerdict] = useState(null);
  const [shareUrl, setShareUrl] = useState(null);

  const scrollRef = useRef(null);
  const messagesContainerRef = useRef(null);

  useEffect(() => {
    // Scroll only within the messages container, not the whole page
    if (messagesContainerRef.current) {
      messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
    }
  }, [phase, clashRounds, isLoading]);

  const getToken = () => localStorage.getItem('token');

  const getRespondingGods = () => {
    const gods = ['athena', 'hermes', 'apollo'];
    if (summonedCouncilGods?.length > 0) {
      for (const g of summonedCouncilGods) {
        if (!gods.includes(g)) gods.push(g);
      }
    }
    return gods;
  };

  // ═══════════════════════════════════════════
  // PHASE 1: Start Debate → Briefing
  // ═══════════════════════════════════════════
  const startDebate = async () => {
    if (!topic.trim()) return;
    setIsLoading(true);
    setError(null);

    try {
      const res = await fetch(`${API_URL}/api/war-council/warroom/start`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${getToken()}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ topic: topic.trim(), responding_gods: getRespondingGods() }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed to start debate');

      setSessionId(data.session_id);
      setDisplayContext(data.display_context);
      setBriefing(data.briefing);
      setPhase('briefing');
      setPhasesCompleted(['briefing']);
    } catch (e) {
      setError(e.message);
    } finally {
      setIsLoading(false);
    }
  };

  // ═══════════════════════════════════════════
  // PHASE 2: Positions
  // ═══════════════════════════════════════════
  const advanceToPositions = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const res = await fetch(`${API_URL}/api/war-council/warroom/${sessionId}/positions`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${getToken()}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed to generate positions');

      setPositions(data.positions);
      setPhase('positions');
      setPhasesCompleted(prev => [...new Set([...prev, 'positions'])]);
    } catch (e) {
      setError(e.message);
    } finally {
      setIsLoading(false);
    }
  };

  // ═══════════════════════════════════════════
  // PHASE 3: Clash
  // ═══════════════════════════════════════════
  const advanceToClash = async (userInterjection = null) => {
    setIsLoading(true);
    setError(null);

    try {
      const res = await fetch(`${API_URL}/api/war-council/warroom/${sessionId}/clash`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${getToken()}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_interjection: userInterjection }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed to generate clash');

      setClashRounds(prev => [...prev, data.clash_round]);
      setPhase('clash');
      setPhasesCompleted(prev => [...new Set([...prev, 'clash'])]);
    } catch (e) {
      setError(e.message);
    } finally {
      setIsLoading(false);
    }
  };

  // ═══════════════════════════════════════════
  // PHASE 4: Verdict
  // ═══════════════════════════════════════════
  const callVerdict = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const res = await fetch(`${API_URL}/api/war-council/warroom/${sessionId}/verdict`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${getToken()}`, 'Content-Type': 'application/json' },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed to generate verdict');

      setVerdict(data.verdict);
      setPhase('verdict');
      setPhasesCompleted(prev => [...new Set([...prev, 'verdict'])]);
    } catch (e) {
      setError(e.message);
    } finally {
      setIsLoading(false);
    }
  };

  // Save to Archives
  const saveToArchives = async () => {
    try {
      const token = getToken();
      const gods = getRespondingGods();
      if (!gods.includes('eris')) gods.push('eris');

      // Reconstruct messages for archive
      const archiveMessages = [];
      archiveMessages.push({ role: 'user', content: topic });
      if (briefing) {
        for (const b of briefing) {
          archiveMessages.push({ role: 'assistant', agent: b.god_id, content: `[BRIEFING] ${b.content}` });
        }
      }
      if (positions) {
        for (const p of positions) {
          archiveMessages.push({ role: 'assistant', agent: p.god_id, content: `[POSITION] ${p.thesis}` });
        }
      }
      for (const round of clashRounds) {
        for (const c of round) {
          archiveMessages.push({ role: 'assistant', agent: c.god_id, content: `[CLASH] ${c.content}` });
        }
      }

      await fetch(`${API_URL}/api/war-council/debates/save`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: `War Room: ${topic.slice(0, 80)}`,
          messages: archiveMessages,
          participating_gods: gods,
          council_recommendation: verdict?.summary || '',
          tags: ['war-room'],
        }),
      });
      alert('War Room session saved to Archives!');
    } catch (e) {
      console.error('Save error:', e);
    }
  };

  // Share War Room Brief
  const shareWarRoom = async () => {
    if (!sessionId) return;
    try {
      const res = await fetch(`${API_URL}/api/war-council/warroom/share`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${getToken()}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ session_id: sessionId }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed to create share link');
      // Use OG-friendly URL for social sharing (auto-redirects to React page)
      const ogShareUrl = `/api/share/warroom/${data.share_id}`;
      setShareUrl(ogShareUrl);
      navigator.clipboard.writeText(API_URL + ogShareUrl);
      alert('Share link copied to clipboard!');
    } catch (e) {
      console.error('Share error:', e);
    }
  };

  const resetSession = () => {
    setPhase(null);
    setSessionId(null);
    setTopic('');
    setDisplayContext(null);
    setBriefing(null);
    setPositions(null);
    setClashRounds([]);
    setVerdict(null);
    setShareUrl(null);
    setPhasesCompleted([]);
    setError(null);
  };

  // ═══════════════════════════════════════════
  // RENDER
  // ═══════════════════════════════════════════

  return (
    <div className="bg-gray-900/90 backdrop-blur-sm rounded-xl border border-red-500/20 flex flex-col h-full overflow-hidden" data-testid="war-room-session">
      {/* Header */}
      <div className="px-4 py-3 border-b border-gray-800 flex items-center justify-between bg-gradient-to-r from-gray-900 to-gray-900/80 flex-shrink-0">
        <div className="flex items-center gap-3">
          <button
            onClick={onExit}
            className="p-1.5 hover:bg-gray-700/50 rounded-lg transition-colors"
            data-testid="exit-war-room-btn"
          >
            <ArrowLeft className="w-4 h-4 text-gray-400" />
          </button>
          <div>
            <h3 className="font-bold text-white text-sm flex items-center gap-2">
              <span className="text-red-400">WAR ROOM</span>
              {phase && <span className="text-gray-600">|</span>}
              {phase && <span className="text-gray-400 font-normal text-xs capitalize">{phase}</span>}
            </h3>
            <p className="text-[10px] text-gray-500">Structured Strategic Debate</p>
          </div>
        </div>
        {sessionId && (
          <div className="flex items-center gap-1.5 px-2 py-1 bg-red-500/10 border border-red-500/20 rounded-full">
            <div className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
            <span className="text-[10px] text-red-400 font-medium hidden sm:inline">Session Active</span>
          </div>
        )}
      </div>

      {/* Phase Bar */}
      {phase && (
        <div className="border-b border-gray-800/50 flex-shrink-0">
          <DebatePhaseBar currentPhase={phase} phasesCompleted={phasesCompleted} />
        </div>
      )}

      {/* Content Area */}
      <div ref={messagesContainerRef} className="flex-1 overflow-y-auto p-4 space-y-4">
        {/* Error Banner */}
        {error && (
          <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-3 text-sm text-red-400">
            {error}
          </div>
        )}

        {/* Topic Input (no phase yet) */}
        {!phase && (
          <div className="flex flex-col items-center justify-center h-full min-h-[300px] gap-6">
            <div className="text-center">
              <div className="text-4xl mb-3">⚔️</div>
              <h2 className="text-xl font-bold text-white mb-1">Enter The War Room</h2>
              <p className="text-sm text-gray-400 max-w-md">
                Present your strategic challenge. The gods will brief, debate, and deliver a structured verdict.
              </p>
            </div>

            <div className="w-full max-w-lg space-y-3">
              <textarea
                value={topic}
                onChange={(e) => setTopic(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey && !isLoading) {
                    e.preventDefault();
                    startDebate();
                  }
                }}
                placeholder="What strategic challenge should the gods debate?&#10;e.g., Should we pivot our pricing strategy to usage-based?"
                rows={3}
                className="w-full bg-gray-800/50 border border-gray-700 rounded-xl px-4 py-3 text-white placeholder-gray-500 text-sm focus:border-red-500/50 focus:ring-2 focus:ring-red-500/20 focus:outline-none resize-none"
                data-testid="war-room-topic-input"
                autoFocus
              />
              <button
                onClick={startDebate}
                disabled={!topic.trim() || isLoading}
                className="w-full py-3 bg-gradient-to-r from-red-600 to-orange-600 hover:from-red-500 hover:to-orange-500 disabled:opacity-50 disabled:cursor-not-allowed text-white font-bold rounded-xl transition-all shadow-lg shadow-red-500/20 hover:shadow-red-500/30 flex items-center justify-center gap-2"
                data-testid="start-debate-btn"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Assembling the War Room...
                  </>
                ) : (
                  <>Convene The War Room</>
                )}
              </button>
            </div>
          </div>
        )}

        {/* Phase 1: Briefing */}
        {phase === 'briefing' && (
          <BriefingPanel
            displayContext={displayContext}
            briefing={briefing}
            topic={topic}
            onProceed={advanceToPositions}
            isLoading={isLoading}
          />
        )}

        {/* Phase 2: Positions */}
        {phase === 'positions' && (
          <PositionsPanel
            positions={positions}
            onProceed={() => advanceToClash(null)}
            isLoading={isLoading}
          />
        )}

        {/* Phase 3: Clash */}
        {phase === 'clash' && (
          <ClashView
            clashRounds={clashRounds}
            onAnotherRound={(interjection) => advanceToClash(interjection)}
            onCallVerdict={callVerdict}
            isLoading={isLoading}
          />
        )}

        {/* Phase 4: Verdict */}
        {phase === 'verdict' && (
          <VerdictPanel
            verdict={verdict}
            onSave={saveToArchives}
            onNewSession={resetSession}
            onShare={shareWarRoom}
            shareUrl={shareUrl}
          />
        )}

        {/* Loading indicator during phase transitions */}
        {isLoading && phase && (
          <div className="flex items-center justify-center gap-3 py-4">
            <div className="w-5 h-5 border-2 border-purple-400/30 border-t-purple-400 rounded-full animate-spin" />
            <span className="text-sm text-gray-400 italic">
              {phase === 'briefing' && 'Gods are reviewing intelligence...'}
              {phase === 'positions' && 'Gods are formulating positions...'}
              {phase === 'clash' && 'The debate intensifies...'}
              {phase === 'verdict' && 'Rendering the verdict...'}
              {!phase && 'Assembling the War Room...'}
            </span>
          </div>
        )}
      </div>

      <style>{`
        @keyframes fade-in {
          from { opacity: 0; transform: translateY(8px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fade-in { animation: fade-in 0.3s ease-out forwards; }
      `}</style>
    </div>
  );
};

export default WarRoomSession;
